import ConstList from '@/ConstList'

// ダウンロードが失敗したときの、案内と記録の共通処理。
//
// もとは旧4帳票（勤務表・月次集計・経費・通勤費）のmixinの中にあった。
// 売上管理の請求書と、プロジェクト集計のPDFは独自実装のままで、失敗すると
// 何が起きても「システムエラーです。管理者に問い合わせてください。」だった。
// 期限切れ・権限・対象なしが全部同じ文言になるので、利用者は原因も対処も
// 分からないまま管理者へ連絡するしかない。
//
// 状態の対応表を2か所に持つと片方だけ直る。ここへ出して両方から使う。

export function statusOf (error) {
  return error && error.response ? Number(error.response.status) || 0 : 0
}

/**
 * 何が起きたのかを、利用者が次にすることが分かる形にする。
 *
 * サーバが返した本文は見せない。共通の例外処理は
 *
 *   IllegalArgumentException → 400
 *   IllegalStateException    → 409
 *
 * のように例外メッセージをそのまま応答へ載せる。内容を選んでいないので、
 * 解析の技術的な文言が本文へ出ることがある。本来は「利用者へ見せてよい」と
 * 明示したエラーコードだけ通したいが、いまの応答は status と message しか
 * 持たず、判断の根拠になるコードが無い。コードが入るまでは固定文言に寄せる。
 */
export function messageForStatus (status) {
  if (status === 401) return ConstList.SESSION_EXPIRED_ERROR
  if (status === 403) return ConstList.DOWNLOAD_FORBIDDEN_ERROR
  if (status === 404) return ConstList.DOWNLOAD_NOT_FOUND_ERROR
  if (status === 400 || status === 422) return ConstList.DOWNLOAD_INPUT_ERROR
  if (status === 409) return ConstList.DOWNLOAD_CONFLICT_ERROR
  // 状態が取れないのは、そもそも応答が届いていない。システム側の異常とは
  // 言い切れないので通信エラーとして案内する。
  if (!status) return ConstList.SYSTEM_COMMUNICATION_ERROR
  return ConstList.SYSTEM_ERROR
}

/**
 * 403 だけは記録しない。SessionFilter が利用者・契約企業・メソッド・パスを
 * すでに残しているので、二重に書いても増えるのはノイズだけ。
 *
 * 401 は記録する。SessionFilter はセッションが無い401をログに残さないため、
 * ここで捨てると「F5で直った」という報告を次回も追えない。
 */
export function shouldReport (status) {
  return status !== 403
}

export function describeFailure (error, status, event) {
  const url = error && error.config ? String(error.config.url || '') : ''
  return [
    `event=${event}`,
    `status=${status || 'none'}`,
    url ? `url=${url}` : '',
    String(error)
  ].filter(Boolean).join(' ')
}

function alertMessage (message) {
  if (typeof window !== 'undefined' && typeof window.alert === 'function') {
    window.alert(message)
  }
}

/**
 * 案内を先に出し、記録はそのあとに回す。
 *
 * 以前は記録の成否で案内を切り替えていた。そのため「対象のデータが
 * 見つかりませんでした」のような本来出すべき案内が、記録の通信が失敗した
 * だけで「システム通信エラー」へ置き換わっていた。記録はこちらの都合で
 * あって、利用者への案内を邪魔してはいけない。
 *
 * 案内を出してよいかを答える関数（shouldShow）を渡せる。頼んだあとで画面が別の月などへ移っていたら
 * （請求書管理の表示の世代。設計書 14.9・監査 RE-3）、案内だけを出さず、記録は今までどおり送る。
 * 渡さない呼び出しは、今までどおりいつも案内を出す。
 *
 * @param {object} http    axios互換のインスタンス
 * @param {object} error   捕まえたエラー
 * @param {string} event   記録に残す事象名。経路ごとに分ける
 * @param {Function} [shouldShow] 案内を出してよいか（いまも頼んだときの画面か）。省略ならいつも出す
 * @returns {Promise}      記録の成否にかかわらず解決する
 */
export function reportDownloadFailure (http, error, event, shouldShow) {
  const status = statusOf(error)

  if (typeof shouldShow !== 'function' || shouldShow()) {
    alertMessage(messageForStatus(status))
  }

  if (!shouldReport(status)) return Promise.resolve(undefined)

  return http
    .post(
      `${process.env.VUE_APP_API_ORIGIN}/error/frontError`,
      { error: describeFailure(error, status, event) },
      { headers: { 'Content-Type': 'application/json' } }
    )
    .catch(() => undefined)
}

export default {
  statusOf,
  messageForStatus,
  shouldReport,
  describeFailure,
  reportDownloadFailure
}
